const API = (import.meta.env.VITE_SCORES_API as string | undefined) ?? '/api/scores';
const KEY_ID = 'solta.player.id';
const KEY_NAME = 'solta.player.name';
const KEY_RUNS = 'solta.runs.local';
const TIMEOUT_MS = 4000;
const MAX_NAME = 14;
const MAX_LOCAL = 200;

export type BoardScope = 'day' | 'all';
export type BoardSource = 'global' | 'local';

export interface BoardRow {
  rank: number;
  name: string;
  score: number;
  accuracy: number;
  errorMs: number;
  multiplier: number;
  at: number;
  you: boolean;
}

export interface BoardPage {
  rows: BoardRow[];
  source: BoardSource;
  offline: boolean;
}

export interface RunResult {
  seed: string;
  score: number;
  judged: number;
  perfects: number;
  accuracy: number;
  errorMs: number;
  multiplier: number;
}

export interface SubmitResult {
  source: BoardSource;
  rank: number | null;
  best: number;
}

interface StoredRun extends RunResult {
  name: string;
  at: number;
}

function store(): Storage | null {
  try {
    const probe = window.localStorage;
    probe.setItem('solta.probe', '1');
    probe.removeItem('solta.probe');
    return probe;
  } catch {
    return null;
  }
}

const memory = new Map<string, string>();

function read(key: string): string | null {
  return store()?.getItem(key) ?? memory.get(key) ?? null;
}

function write(key: string, value: string): void {
  const disk = store();
  if (disk === null) memory.set(key, value);
  else disk.setItem(key, value);
}

function printable(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  const invisible = code >= 0x200b && code <= 0x200f;
  return code >= 0x20 && !(code >= 0x7f && code <= 0x9f) && !invisible && code !== 0x2028 && code !== 0x2029;
}

export function cleanName(raw: string): string {
  return [...raw]
    .filter(printable)
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_NAME);
}

export function clientId(): string {
  const known = read(KEY_ID);
  if (known !== null && known.length >= 8) return known;
  const fresh =
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `solta-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
  write(KEY_ID, fresh);
  return fresh;
}

export function playerName(): string | null {
  const name = read(KEY_NAME);
  return name === null ? null : cleanName(name) || null;
}

export function setPlayerName(raw: string): string {
  const name = cleanName(raw) || 'anônimo';
  write(KEY_NAME, name);
  return name;
}

function localRuns(): StoredRun[] {
  const raw = read(KEY_RUNS);
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as StoredRun[]) : [];
  } catch {
    return [];
  }
}

function keepLocal(run: StoredRun): void {
  const kept = [...localRuns(), run]
    .sort((a, b) => b.score - a.score || a.errorMs - b.errorMs)
    .slice(0, MAX_LOCAL);
  write(KEY_RUNS, JSON.stringify(kept));
}

export function localBest(seed: string | null): number {
  const pool = seed === null ? localRuns() : localRuns().filter((run) => run.seed === seed);
  return pool.reduce((best, run) => Math.max(best, run.score), 0);
}

function localPage(scope: BoardScope, seed: string, limit: number): BoardPage {
  const pool = scope === 'all' ? localRuns() : localRuns().filter((run) => run.seed === seed);
  const rows = pool
    .sort((a, b) => b.score - a.score || a.errorMs - b.errorMs)
    .slice(0, limit)
    .map((run, index) => ({
      rank: index + 1,
      name: run.name,
      score: run.score,
      accuracy: run.accuracy,
      errorMs: run.errorMs,
      multiplier: run.multiplier,
      at: run.at,
      you: true,
    }));
  return { rows, source: 'local', offline: true };
}

function asRows(raw: unknown): BoardRow[] | null {
  if (!Array.isArray(raw)) return null;
  return raw.flatMap((item): BoardRow[] => {
    if (typeof item !== 'object' || item === null) return [];
    const row = item as Partial<BoardRow>;
    if (typeof row.name !== 'string' || typeof row.score !== 'number') return [];
    return [
      {
        rank: typeof row.rank === 'number' ? row.rank : 0,
        name: row.name,
        score: row.score,
        accuracy: typeof row.accuracy === 'number' ? row.accuracy : 0,
        errorMs: typeof row.errorMs === 'number' ? row.errorMs : 0,
        multiplier: typeof row.multiplier === 'number' ? row.multiplier : 1,
        at: typeof row.at === 'number' ? row.at : 0,
        you: row.you === true,
      },
    ];
  });
}

export async function topRuns(scope: BoardScope, seed: string, limit = 20): Promise<BoardPage> {
  const url = `${API}?scope=${scope}&seed=${encodeURIComponent(seed)}&limit=${limit}&client=${encodeURIComponent(clientId())}`;
  try {
    const response = await fetch(url, { signal: AbortSignal.timeout(TIMEOUT_MS) });
    if (!response.ok) return localPage(scope, seed, limit);
    const body = (await response.json()) as { rows?: unknown };
    const rows = asRows(body.rows);
    if (rows === null) return localPage(scope, seed, limit);
    return { rows, source: 'global', offline: false };
  } catch {
    return localPage(scope, seed, limit);
  }
}

export async function submitRun(run: RunResult, rawName: string): Promise<SubmitResult> {
  const name = setPlayerName(rawName);
  keepLocal({ ...run, name, at: Date.now() });
  const best = Math.max(run.score, localBest(run.seed));

  try {
    const response = await fetch(API, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...run, name, clientId: clientId() }),
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
    if (!response.ok) return { source: 'local', rank: null, best };
    const body = (await response.json()) as { ok?: boolean; rank?: number; best?: number };
    if (body.ok !== true) return { source: 'local', rank: null, best };
    return {
      source: 'global',
      rank: typeof body.rank === 'number' ? body.rank : null,
      best: typeof body.best === 'number' ? body.best : best,
    };
  } catch {
    return { source: 'local', rank: null, best };
  }
}
